import type {
	Block,
	DefaultBlockSchema,
	DefaultInlineContentSchema,
	DefaultStyleSchema
} from "@blocknote/core"
import { z } from "zod"

export const mentionInlineContentConfig = {
	type: "mention",
	propSchema: { userId: { default: "" }, name: { default: "" } },
	content: "none"
} as const

export const formAnswerInlineContentConfig = {
	type: "formAnswer",
	propSchema: { fieldId: { default: "" }, label: { default: "" } },
	content: "none"
} as const

export const customEmojiInlineContentConfig = {
	type: "customEmoji",
	propSchema: {
		id: { default: "" },
		name: { default: "emoji" },
		organizationId: { default: "" }
	},
	content: "none"
} as const

export const iframeBlockConfig = {
	type: "iframe",
	propSchema: { url: { default: "" } },
	content: "none"
} as const

export const formEmbedBlockConfig = {
	type: "formEmbed",
	propSchema: { formId: { default: "" } },
	content: "none"
} as const

export const formPageBreakBlockConfig = {
	type: "formPageBreak",
	propSchema: { logic: { default: "" } },
	content: "inline"
} as const

export const questionBlockConfig = {
	type: "question",
	propSchema: {
		qtype: { default: "shortText" },
		description: { default: "" },
		placeholder: { default: "" },
		defaultAnswer: { default: "" },
		defaultAnswerEnabled: { default: false },
		required: { default: false },
		choices: { default: "" },
		allowOther: { default: false },
		includesDate: { default: true },
		includesTime: { default: false },
		min: { default: "" },
		max: { default: "" },
		logic: { default: "" }
	},
	content: "inline"
} as const

export function normalizeIframeUrl(value: unknown) {
	if (typeof value !== "string") return null
	try {
		const url = new URL(value.trim())
		return url.protocol === "https:" && !url.username && !url.password
			? url.href
			: null
	} catch {
		return null
	}
}

const styledText = z.object({
	type: z.literal("text"),
	text: z.string(),
	styles: z.record(z.string(), z.union([z.boolean(), z.string()]))
})
const inlineContent = z.array(
	z.union([
		styledText,
		z.object({
			type: z.literal("formAnswer"),
			props: z.object({ fieldId: z.string(), label: z.string() })
		}),
		z.object({
			type: z.literal("customEmoji"),
			props: z.object({
				id: z.string(),
				name: z.string(),
				organizationId: z.string()
			})
		}),
		z.object({
			type: z.literal("mention"),
			props: z.object({ userId: z.string(), name: z.string() })
		}),
		z.object({
			type: z.literal("link"),
			href: z.string(),
			content: z.array(styledText)
		})
	])
)
const props = z.record(
	z.string(),
	z.union([z.string(), z.number(), z.boolean()])
)
// BlockNote allocates header arrays from stored headerRows/headerCols before
// validating them against row/cell counts, so bounds are enforced here instead of
// trusting stored counts; out-of-range values are dropped rather than rejected so
// normalized legacy documents keep rendering with their headers removed.
const headerCount = z
	.number()
	.optional()
	.transform((value) =>
		value === undefined ||
		(Number.isInteger(value) && value >= 0 && value <= 10)
			? value
			: undefined
	)
const block = z.object({
	id: z.string(),
	type: z.enum([
		"audio",
		"bulletListItem",
		"checkListItem",
		"codeBlock",
		"divider",
		"file",
		"formEmbed",
		"formPageBreak",
		"question",
		"heading",
		"image",
		"iframe",
		"numberedListItem",
		"paragraph",
		"quote",
		"table",
		"toggleListItem",
		"video"
	]),
	props,
	content: z
		.union([
			inlineContent,
			z.object({
				type: z.literal("tableContent"),
				columnWidths: z.array(z.number().nullable().optional()),
				headerRows: headerCount,
				headerCols: headerCount,
				rows: z.array(
					z.object({
						cells: z.array(
							z.union([
								inlineContent,
								z.object({
									type: z.literal("tableCell"),
									props,
									content: inlineContent
								})
							])
						)
					})
				)
			})
		])
		.optional(),
	get children(): z.ZodArray<typeof block> {
		return z.array(block)
	}
})

// Markdown is a portable snapshot for the existing GitHub/Discord integrations.
// Native blocks remain the editing source of truth, preserving all formatting.
export const blockNoteContentSchema = z.object({
	format: z.literal("blocknote"),
	version: z.literal(1),
	blocks: z.array(block).min(1),
	markdown: z.string()
})

export function parseBlockNoteContent(value: string | null | undefined) {
	if (!value) return null
	try {
		const result = blockNoteContentSchema.safeParse(JSON.parse(value))
		if (!result.success) return null
		return {
			...result.data,
			blocks: result.data.blocks as Block<
				DefaultBlockSchema & {
					iframe: typeof iframeBlockConfig
					formEmbed: typeof formEmbedBlockConfig
					formPageBreak: typeof formPageBreakBlockConfig
					question: typeof questionBlockConfig
				},
				DefaultInlineContentSchema & {
					mention: typeof mentionInlineContentConfig
					formAnswer: typeof formAnswerInlineContentConfig
					customEmoji: typeof customEmojiInlineContentConfig
				},
				DefaultStyleSchema
			>[]
		}
	} catch {
		return null
	}
}

export function getBlockNotePlainText(
	blocks: NonNullable<ReturnType<typeof parseBlockNoteContent>>["blocks"]
): string {
	return blocks
		.map((block) => {
			const content = block.content
			const lines =
				content && !Array.isArray(content)
					? content.rows.flatMap((row) =>
							row.cells.map((cell) =>
								Array.isArray(cell) ? cell : cell.content
							)
						)
					: [content ?? []]
			const text = lines
				.map((line) =>
					line
						.map((part) =>
							part.type === "text"
								? part.text
								: part.type === "formAnswer"
									? `{{${part.props.fieldId}}}`
									: part.type === "mention"
										? `@${part.props.name}`
										: part.type === "customEmoji"
											? `:${part.props.name}:`
											: part.content.map((text) => text.text).join("")
						)
						.join("")
				)
				.join("\n")
			const fileText =
				"url" in block.props
					? ("name" in block.props && block.props.name) || block.props.url
					: ""
			return [text || fileText, getBlockNotePlainText(block.children)]
				.filter(Boolean)
				.join("\n")
		})
		.join("\n")
}
